"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SortationAPIClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const errors_1 = require("@deliverr/errors");
const SortationPackage_1 = require("./SortationPackage");
const commons_logs_1 = require("@deliverr/commons-logs");
const SortationCenter_1 = require("./SortationCenter");
const SortCodeMapping_1 = require("./SortCodeMapping");
const SORTATION_BASIC_AUTH_PASSWORD = process.env.SORTATION_BASIC_AUTH_PASSWORD;
const SORTATION_BASIC_AUTH_USER = process.env.SORTATION_BASIC_AUTH_USER;
const SORTATION_URL = process.env.SORTATION_URL;
const MILLISECONDS_PER_MINUTE = 60000;
class SortationAPIClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = {
        baseURL: `${SORTATION_URL}/api`,
        basicAuthUser: SORTATION_BASIC_AUTH_USER,
        basicAuthPassword: SORTATION_BASIC_AUTH_PASSWORD,
    }) {
        super(config);
        if (!SORTATION_URL) {
            commons_logs_1.log.warn("SORTATION_URL environment variable is not set");
        }
        if (!config.basicAuthUser || !config.basicAuthPassword) {
            commons_logs_1.log.warn("SORTATION_BASIC_AUTH_USER and/or SORTATION_BASIC_AUTH_PASSWORD environment variable is not set");
        }
    }
    async createPackage(shippingLabelSummary, sortationCenterId, providerName) {
        const { shipmentId, labelId, shippingMethod, trackingCode, sortCode } = shippingLabelSummary;
        if (!sortCode || !trackingCode) {
            throw new errors_1.DeliverrError({
                code: SortationPackage_1.SortationPackageError.INVALID_SHIPPING_LABEL_INFO,
            });
        }
        try {
            const shippingLabelInfo = {
                labelId,
                providerName,
                shipmentId,
                shippingMethod,
                sortationCenterId,
                sortCode,
                trackingCode,
            };
            return await this.post(`/v1/package`, shippingLabelInfo);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                SortationPackage_1.SortationPackageError.SORTATION_PACKAGE_EXISTS,
                SortationPackage_1.SortationPackageError.INVALID_SHIPPING_LABEL_INFO,
            ]);
        }
    }
    async createPackageV2(sortationPackageInfo) {
        try {
            return await this.post(`/v2/package`, sortationPackageInfo);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [SortationPackage_1.SortationPackageError.SORTATION_PACKAGE_EXISTS]);
        }
    }
    async updatePackage(sortationPackageId, shippingLabelInfo) {
        try {
            return await this.put(`/v1/package/${sortationPackageId}`, shippingLabelInfo);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [SortationPackage_1.SortationPackageError.SORTATION_PACKAGE_NOT_FOUND]);
        }
    }
    async updatePackageV2(sortationPackageId, sortationPackageInfo) {
        try {
            return await this.put(`/v2/package/${sortationPackageId}`, sortationPackageInfo);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [SortationPackage_1.SortationPackageError.SORTATION_PACKAGE_NOT_FOUND]);
        }
    }
    async cancelPackage(sortationPackageId) {
        try {
            return await this.delete(`/v1/package/${sortationPackageId}`, undefined);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                SortationPackage_1.SortationPackageError.CANCELLATION_FAILED_SCANNED_PACKAGE,
                SortationPackage_1.SortationPackageError.SORTATION_PACKAGE_NOT_FOUND,
            ]);
        }
    }
    async createOrUpdateZoneSkipLane(newLane) {
        try {
            await this.put("/v1/zone-skip-lane", newLane);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getPackagesByTrackingCodeAndShipmentId(trackingCode, shipmentId) {
        const url = `/v1/package/${trackingCode}/shipmentId/${shipmentId}`;
        try {
            return await this.get(url);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [SortationPackage_1.SortationPackageError.SORTATION_PACKAGE_NOT_FOUND]);
        }
    }
    async getSortationCenters() {
        try {
            return await this.getCached("/v1/sortation-center", undefined, 10 * MILLISECONDS_PER_MINUTE);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getSortCodeMapping() {
        try {
            return await this.get("/v1/ops/mapping/sort-code");
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [SortCodeMapping_1.MappingError.UNABLE_TO_RETRIEVE_SORT_CODE_MAPPING]);
        }
    }
    async getSortCodeZipMapping(sortCode, sortationCenter, shippingMethod) {
        try {
            return await this.get(`/v1/ops/mapping/sort-code/${sortCode}`, {
                sortationCenter,
                shippingMethod,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [SortCodeMapping_1.MappingError.UNABLE_TO_RETRIEVE_SORT_CODE_MAPPING]);
        }
    }
    async createOrUpdateSortCodeMapping(sortationCenterId, shippingMethod, sortCode, zipCodes, reason) {
        try {
            return await this.post(`/v1/ops/mapping/sort-code`, {
                sortationCenterId,
                shippingMethod,
                sortCode,
                zipCodes,
                reason,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                SortationCenter_1.SortationCenterError.SORTATION_CENTER_NOT_FOUND,
                SortCodeMapping_1.MappingError.FAILED_TO_CREATE_OR_UPDATE_SORT_CODE_MAPPING,
            ]);
        }
    }
    async deleteSortCodeMapping(sortationCenterId, shippingMethod, sortCode, reason) {
        try {
            return await this.delete(`/v1/ops/mapping/sort-code`, undefined, {
                sortationCenterId,
                shippingMethod,
                sortCode,
                reason,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                SortationCenter_1.SortationCenterError.SORTATION_CENTER_NOT_FOUND,
                SortCodeMapping_1.MappingError.FAILED_TO_DELETE_SORT_CODE_MAPPING,
            ]);
        }
    }
    async getSortCode(shippingMethod, warehouseId, destinationZipCode, sortationCenterId) {
        try {
            const { sortCode } = await this.get("/v1/mapping/sort-code", {
                shippingMethod,
                warehouseId,
                destinationZipCode,
                sortationCenterId,
            });
            return sortCode;
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getZoneSkipLanes() {
        try {
            return await this.getCached("/v1/zone-skip-lane", undefined, 10 * MILLISECONDS_PER_MINUTE);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getSortationCarrierAccounts(queryAttributes) {
        const url = "/v1/sortation-carrier-account";
        try {
            return this.get(url, queryAttributes);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getPackagesAssignedToGaylord(sortationGaylordId) {
        const url = `/v1/gaylord-packages/${sortationGaylordId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
}
exports.SortationAPIClient = SortationAPIClient;
//# sourceMappingURL=SortationAPIClient.js.map